import { Api } from './Api';
import { ApiActor } from './ApiActor';
import { apiFetch } from './apiFetch';
import { ApiRequestOptions } from './ApiRequestOptions';
import { FetchData } from './FetchData';
import { GetEndpointMethodNames } from './GetEndpointMethodNames';
import { GetMethod } from './GetMethod';
import React from 'react';

export type GetUseApi<TApi extends Api<any, any>> = <
  TPath extends TApi['Path'],
  TMethod extends GetEndpointMethodNames<TApi, TPath>,
>(
  path: TPath,
  method: TMethod,
  queryParams?: string,
) => ApiActor<TApi, TPath, TMethod>;

export const createUseApi = <TApi extends Api<any, any>>(
  fetchData: FetchData,
) =>
  function useApi<
    TPath extends TApi['Path'],
    TMethod extends GetEndpointMethodNames<TApi, TPath>,
  >(
    path: TPath,
    method: TMethod,
    queryParams: string | undefined = '',
  ): ApiActor<TApi, TPath, TMethod> {
    type TData = GetMethod<TApi, TPath, TMethod>['Response'];
    const [error, setError] = React.useState<TApi['Error']>();
    const [fetching, setFetching] = React.useState<boolean>(false);
    const [request, setRequest] = React.useState<
      ApiRequestOptions<TApi, TPath, TMethod> | undefined
    >();
    const [data, setData] = React.useState<TData | undefined>();
    const fetch = React.useCallback<ApiActor<TApi, TPath, TMethod>['fetch']>(
      async (options = {}) => {
        setFetching(true);
        setRequest(options);
        try {
          const fullPath = String(path) + queryParams;
          const fetchedData = await apiFetch(
            fullPath,
            method,
            options,
            fetchData,
          );
          setData(fetchedData);
          setFetching(false);
          setError(undefined);
          return fetchedData;
        } catch (error) {
          setError(error);
          setFetching(false);
          throw error;
        }
      },
      [method, path],
    );

    const apiActor = React.useMemo<ApiActor<TApi, TPath, TMethod>>(
      () => ({
        path,
        method,
        fetching,
        fetch,
        data,
        error,
        isLoaded: data !== undefined,
        request: request as any,
      }),
      [path, method, fetching, data, fetch, request, error],
    );

    return apiActor;
  };
